import {
  AUTH_COOKIE_BASE_PREFIX,
  authCookiePrefix,
} from "@vidimo/shared/contracts";
import { emailOTPClient, organizationClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import {
  capacitorClient,
  getCapacitorAuthToken,
  isNativePlatform,
  setCapacitorAuthToken,
} from "better-auth-capacitor/client";
import { getApiUrl } from "@/lib/api-url";
import { env } from "@/lib/env";
import { SUPPORT_SESSION } from "@/lib/support-session";

const isNative = isNativePlatform();

// Web dev: leave `baseURL` empty so requests go to relative paths like
// `/api/auth/...` (the same-origin API prefix, `lib/api-url.ts`) and Vite's
// proxy forwards them to the API server at the
// fixed `localhost:3001` (see apps/mobile/vite.config.mts).
// Native (Capacitor) builds bypass the proxy and must talk to an absolute
// URL set via VITE_API_URL at build time.
const explicitApiUrl = env.VITE_API_URL?.trim();
const AUTH_PATH = "/auth";
const baseURL = explicitApiUrl || "";
if (isNative && !explicitApiUrl) {
  console.warn(
    "[boot] auth-client: native build without VITE_API_URL — relative URLs won't resolve. Set VITE_API_URL when building for Capacitor."
  );
}

/** The post-sign-in landing, and so the default web callback target. */
const DEFAULT_WEB_CALLBACK_PATH = "/chats";

/**
 * Post-auth redirect target: the native deep-link back into the app on
 * Capacitor, else `webPath` on the app's own origin. Shared by every auth entry
 * point (sign-in, sign-up, Apple) and by account linking, which passes the
 * screen the user started from so the round-trip returns them there rather than
 * to the sign-in landing.
 */
export function getAuthCallbackUrl(
  webPath: string = DEFAULT_WEB_CALLBACK_PATH
): string {
  return isNative
    ? "vidimo://auth/callback"
    : `${window.location.origin}${webPath}`;
}

/**
 * Cookies on the web, except in a support tab: its session is the bearer
 * token it was granted, and the browser's own cookie for the app, which is
 * the agent's, is never sent (lib/support-session.ts).
 */
function webFetchOptions() {
  return SUPPORT_SESSION
    ? {
        auth: { token: SUPPORT_SESSION.token, type: "Bearer" as const },
        credentials: "omit" as const,
      }
    : { credentials: "include" as const };
}

/** Where the native auth plugin keeps the session token. */
export const AUTH_STORAGE_PREFIX = "superhost";

/** The API's auth cookie prefix, which the native plugin stores the session under. */
const AUTH_COOKIE_PREFIX = authCookiePrefix(
  explicitApiUrl?.startsWith("https://") === true
);

/** The native session token, for the requests the auth client does not send. */
export function getNativeSessionToken(): Promise<string | null> {
  return getCapacitorAuthToken({
    cookiePrefix: AUTH_COOKIE_PREFIX,
    storagePrefix: AUTH_STORAGE_PREFIX,
  });
}

/**
 * Copies a token held under the plugin's default cookie names to the `__Host-`
 * name it reads, so a device holding one stays signed in. The session is valid
 * as a bearer, and native storage holds only what this API set.
 */
export async function carryNativeSessionTokenOver(): Promise<void> {
  if (!isNative || (await getNativeSessionToken())) {
    return;
  }
  const token = await getCapacitorAuthToken({
    storagePrefix: AUTH_STORAGE_PREFIX,
  });
  if (token) {
    await setCapacitorAuthToken({
      cookiePrefix: AUTH_COOKIE_PREFIX,
      storagePrefix: AUTH_STORAGE_PREFIX,
      token,
    });
  }
}

export const authClient = createAuthClient({
  basePath: explicitApiUrl ? AUTH_PATH : getApiUrl(AUTH_PATH),
  baseURL,
  ...(isNative ? {} : { fetchOptions: webFetchOptions() }),
  disableDefaultFetchPlugins: isNative,
  plugins: [
    organizationClient(),
    emailOTPClient(),
    // The inert `{ id }` cast silences ONLY the plugin's type contribution:
    // better-auth-capacitor's client plugin is typed against an older
    // @better-auth/core, and letting its $InferServerPlugin into
    // createAuthClient's generics collapses the inferred session to `never`
    // and drops the organization plugin's route proxies (setActive,
    // acceptInvitation). The runtime plugin is untouched, and no app code
    // consumes capacitor-specific client types.
    capacitorClient({
      // The plugin reads its token under the first prefix, and matches a
      // response's cookies on their name stripped of `__Host-`, so it needs
      // the bare one too.
      cookiePrefix: [AUTH_COOKIE_PREFIX, AUTH_COOKIE_BASE_PREFIX],
      scheme: "superhost",
      storagePrefix: AUTH_STORAGE_PREFIX,
    }) as unknown as { id: "capacitor" },
  ],
});

export const { signOut, useSession } = authClient;
